'use client';
import { jsx } from 'react/jsx-runtime';
import { vars } from '@seed-design/css/vars';
import { forwardRef } from 'react';

function handleColor(color) {
  if (!color) {
    return void 0;
  }
  const [type, value] = color.split(".");
  return vars.$color[type]?.[value] ?? color;
}
function handleDimension(dimension) {
  if (dimension == null) {
    return void 0;
  }
  if (typeof dimension === "number") {
    return `${dimension}px`;
  }
  if (dimension === "full") {
    return "100%";
  }
  const [type, value] = dimension.split(".");
  return vars.$dimension[dimension] ?? vars.$dimension[type]?.[value] ?? dimension;
}
function handleRadius(radius) {
  if (radius == null) {
    return void 0;
  }
  return vars.$radius[radius] ?? radius;
}
function handleDisplay(display) {
  if (!display) {
    return void 0;
  }
  if (process.env.NODE_ENV !== "production") {
    if (display === "inlineFlex" || display === "inlineBlock") {
      console.warn(
        `[SEED Design System] ${display} is deprecated. Use inline-flex or inline-block instead.`
      );
    }
  }
  return {
    flex: "flex",
    inlineFlex: "inline-flex",
    // @deprecated Use `inline-flex` instead.
    inlineBlock: "inline-block",
    // @deprecated Use `inline-block` instead.
    none: "none"
  }[display] ?? display;
}
function handleFlexDirection(flexDirection) {
  if (!flexDirection) {
    return void 0;
  }
  if (process.env.NODE_ENV !== "production") {
    if (flexDirection === "rowReverse" || flexDirection === "columnReverse") {
      console.warn(
        `[SEED Design System] ${flexDirection} is deprecated. Use row-reverse or column-reverse instead.`
      );
    }
  }
  return {
    row: "row",
    column: "column",
    rowReverse: "row-reverse",
    // @deprecated Use `row-reverse` instead.
    columnReverse: "column-reverse"
    // @deprecated Use `column-reverse` instead.
  }[flexDirection] ?? flexDirection;
}
function handleJustifyContent(justifyContent) {
  if (!justifyContent) {
    return void 0;
  }
  if (process.env.NODE_ENV !== "production") {
    if (justifyContent === "flexStart" || justifyContent === "flexEnd") {
      console.warn(
        `[SEED Design System] ${justifyContent} is deprecated. Use flex-start or flex-end instead.`
      );
    }
    if (justifyContent === "spaceBetween" || justifyContent === "spaceAround") {
      console.warn(
        `[SEED Design System] ${justifyContent} is deprecated. Use space-between or space-around instead.`
      );
    }
  }
  return {
    flexStart: "flex-start",
    // @deprecated Use `flex-start` instead.
    flexEnd: "flex-end",
    // @deprecated Use `flex-end` instead.
    center: "center",
    spaceBetween: "space-between",
    // @deprecated Use `space-between` instead.
    spaceAround: "space-around"
    // @deprecated Use `space-around` instead.
  }[justifyContent] ?? justifyContent;
}
function handleAlignItems(alignItems) {
  if (!alignItems) {
    return void 0;
  }
  if (process.env.NODE_ENV !== "production") {
    if (alignItems === "flexStart" || alignItems === "flexEnd") {
      console.warn(
        `[SEED Design System] ${alignItems} is deprecated. Use flex-start or flex-end instead.`
      );
    }
  }
  return {
    flexStart: "flex-start",
    // @deprecated Use `flex-start` instead.
    flexEnd: "flex-end",
    // @deprecated Use `flex-end` instead.
    center: "center",
    stretch: "stretch"
  }[alignItems] ?? alignItems;
}
function useStyleProps(props) {
  const {
    background,
    bg,
    color,
    borderColor,
    borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    bottom,
    left,
    right,
    top,
    display,
    position,
    overflowX,
    overflowY,
    zIndex,
    flexGrow,
    flexShrink,
    flexDirection,
    flexWrap,
    justifyContent,
    alignItems,
    alignContent,
    alignSelf,
    gap,
    unstable_transform,
    style,
    ...restProps
  } = props;
  return {
    style: {
      "--seed-box-background": handleColor(background ?? bg),
      "--seed-box-color": handleColor(color),
      "--seed-box-border-color": handleColor(borderColor),
      "--seed-box-border-width": borderWidth !== void 0 ? `${borderWidth}px` : void 0,
      "--seed-box-border-top-width": borderTopWidth !== void 0 ? `${borderTopWidth}px` : void 0,
      "--seed-box-border-right-width": borderRightWidth !== void 0 ? `${borderRightWidth}px` : void 0,
      "--seed-box-border-bottom-width": borderBottomWidth !== void 0 ? `${borderBottomWidth}px` : void 0,
      "--seed-box-border-left-width": borderLeftWidth !== void 0 ? `${borderLeftWidth}px` : void 0,
      "--seed-box-border-radius": handleRadius(borderRadius),
      "--seed-box-border-top-left-radius": handleRadius(borderTopLeftRadius),
      "--seed-box-border-top-right-radius": handleRadius(borderTopRightRadius),
      "--seed-box-border-bottom-right-radius": handleRadius(borderBottomRightRadius),
      "--seed-box-border-bottom-left-radius": handleRadius(borderBottomLeftRadius),
      "--seed-box-width": handleDimension(width),
      "--seed-box-min-width": handleDimension(minWidth),
      "--seed-box-max-width": handleDimension(maxWidth),
      "--seed-box-height": handleDimension(height),
      "--seed-box-min-height": handleDimension(minHeight),
      "--seed-box-max-height": handleDimension(maxHeight),
      "--seed-box-padding": handleDimension(padding ?? p),
      "--seed-box-padding-x": handleDimension(paddingX ?? px),
      "--seed-box-padding-y": handleDimension(paddingY ?? py),
      "--seed-box-padding-top": handleDimension(paddingTop ?? pt),
      "--seed-box-padding-right": handleDimension(paddingRight ?? pr),
      "--seed-box-padding-bottom": handleDimension(paddingBottom ?? pb),
      "--seed-box-padding-left": handleDimension(paddingLeft ?? pl),
      "--seed-box-top": top,
      "--seed-box-left": left,
      "--seed-box-right": right,
      "--seed-box-bottom": bottom,
      "--seed-box-gap": handleDimension(gap),
      "--seed-box-display": handleDisplay(display),
      "--seed-box-position": position,
      "--seed-box-overflow-x": overflowX,
      "--seed-box-overflow-y": overflowY,
      "--seed-box-z-index": zIndex,
      "--seed-box-flex-grow": flexGrow,
      "--seed-box-flex-shrink": flexShrink,
      "--seed-box-flex-direction": handleFlexDirection(flexDirection),
      "--seed-box-flex-wrap": flexWrap,
      "--seed-box-justify-content": handleJustifyContent(justifyContent),
      "--seed-box-align-items": handleAlignItems(alignItems),
      "--seed-box-align-content": handleAlignItems(alignContent),
      "--seed-box-align-self": handleAlignItems(alignSelf),
      "--seed-box-unstable-transform": unstable_transform,
      ...style
    },
    restProps
  };
}
function withStyleProps(Component) {
  const Node = forwardRef((props, ref) => {
    const { style, restProps } = useStyleProps(props);
    return /* @__PURE__ */ jsx(Component, { ref, style, ...restProps });
  });
  Node.displayName = Component.displayName || Component.name;
  return Node;
}

export { handleColor, handleDimension, useStyleProps, withStyleProps };
